import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import type { SwaggerResponse } from '@/services/config';

import type { ApiError } from './errors';

/** A query as the UI sees it: `data` is the mapped body, never the axios response. */
export interface DomainQuery<T> {
    data: T | undefined;
    /**
     * No data yet, whether or not a request is running: react-query's `isPending`. Its own
     * `isLoading` also needs a request in flight, so a disabled query with nothing would read as
     * loaded.
     */
    isLoading: boolean;
    isFetching: boolean;
    isFetched: boolean;
    isSuccess: boolean;
    isError: boolean;
    error: ApiError | null;
    refetch: () => Promise<unknown>;
}

/**
 * Unwraps a generated query's axios response and maps its body. The cache keeps the raw
 * response under the generated key, so other readers of that key see no change. `map` must be
 * stable (a module-level function), or the mapped data is rebuilt on every render.
 */
export function useMappedQuery<TBody, TData>(
    query: UseQueryResult<SwaggerResponse<TBody>, ApiError>,
    map: (body: TBody) => TData
): DomainQuery<TData> {
    const body = query.data?.data;
    const data = useMemo(() => (body === undefined ? undefined : map(body)), [body, map]);

    return {
        data,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        isFetched: query.isFetched,
        isSuccess: query.isSuccess,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}
